import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, HeartIcon, ShareIcon, MapPinIcon, ShieldCheckIcon, SparklesIcon, MessageSquareIcon, PhoneIcon, CheckCircle2Icon, SendIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const mockListing = {
  id: '1',
  title: 'Toyota Land Cruiser Prado GXR 2018',
  price: 'AED 82,000',
  location: 'Al Quoz, Dubai',
  images: ['https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=1200', 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=1200', 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&q=80&w=1200'],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '112,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Engine',
    value: '4.0L V6'
  }, {
    label: 'Transmission',
    value: 'Automatic'
  }, {
    label: 'Exterior Color',
    value: 'Pearl White'
  }, {
    label: 'Interior Color',
    value: 'Beige'
  }, {
    label: 'Body Type',
    value: 'SUV'
  }],
  matchScore: 98,
  seller: {
    name: 'Al Futtaim Automall',
    type: 'Verified Business',
    joined: '2015',
    responseRate: '99%',
    responseTime: 'within 1 hour'
  },
  description: 'Excellent condition Toyota Prado GXR 2018. Full service history available. No accidents, original paint. Comes with 1 year warranty and free service contract for 20,000 km. Finance options available.',
  aiSummary: ['Fair price based on market comparison (avg AED 84k)', 'GCC specs confirmed via VIN', 'Mileage is reasonable for the model year', 'Seller response rate is exceptionally high']
};
export function ListingDetail() {
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiChat, setAiChat] = useState<{
    role: 'user' | 'ai';
    content: string;
  }[]>([]);
  const handleAskAI = (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;
    setAiChat([...aiChat, {
      role: 'user',
      content: aiQuery
    }]);
    const query = aiQuery;
    setAiQuery('');
    setTimeout(() => {
      let response = 'Based on the listing details, this is a very competitive offer. The price is slightly below market average for a 2018 GXR with this mileage.';
      if (query.toLowerCase().includes('check')) {
        response = 'Before buying, you should check the service history book, inspect the tires (they usually need replacement around 100k km), and verify the warranty terms mentioned in the description.';
      }
      setAiChat(prev => [...prev, {
        role: 'ai',
        content: response
      }]);
    }, 1000);
  };
  return <div className="flex-1 overflow-y-auto bg-slate-50 pb-24 lg:pb-8" data-id="element-2726">
      {/* Top Navigation */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between lg:hidden" data-id="element-2727">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-2728">
          <ChevronLeftIcon className="w-6 h-6 text-slate-700" data-id="element-2729" />
        </button>
        <div className="flex gap-2" data-id="element-2730">
          <button className="p-2 hover:bg-slate-100 rounded-full" data-id="element-2731">
            <ShareIcon className="w-5 h-5 text-slate-700" data-id="element-2732" />
          </button>
          <button onClick={() => setIsSaved(!isSaved)} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-2733">
            <HeartIcon className={`w-5 h-5 ${isSaved ? 'fill-error text-error' : 'text-slate-700'}`} data-id="element-2734" />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 lg:p-8" data-id="element-2735">
        {/* Desktop Back Button */}
        <button onClick={() => navigate(-1)} className="hidden lg:flex items-center gap-2 text-slate-500 hover:text-primary-indigo mb-6 transition-colors" data-id="element-2736">
          <ChevronLeftIcon className="w-5 h-5" data-id="element-2737" />
          <span data-id="element-2738">Back to results</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2739">
          {/* Left Column: Images & Details */}
          <div className="lg:col-span-2 space-y-8" data-id="element-2740">
            {/* Image Gallery */}
            <div className="space-y-4" data-id="element-2741">
              <div className="aspect-[4/3] lg:aspect-[16/9] rounded-2xl overflow-hidden bg-slate-200 relative group" data-id="element-2742">
                <img src={mockListing.images[activeImage]} alt={mockListing.title} className="w-full h-full object-cover" data-id="element-2743" />

                {/* Desktop Actions Overlay */}
                <div className="hidden lg:flex absolute top-4 right-4 gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2744">
                  <button className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" data-id="element-2745">
                    <ShareIcon className="w-5 h-5 text-slate-700" data-id="element-2746" />
                  </button>
                  <button onClick={() => setIsSaved(!isSaved)} className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" data-id="element-2747">
                    <HeartIcon className={`w-5 h-5 ${isSaved ? 'fill-error text-error' : 'text-slate-700'}`} data-id="element-2748" />
                  </button>
                </div>
              </div>

              <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar" data-id="element-2749">
                {mockListing.images.map((img, idx) => <button key={idx} onClick={() => setActiveImage(idx)} className={`relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2 transition-all ${activeImage === idx ? 'border-primary-indigo' : 'border-transparent opacity-70 hover:opacity-100'}`} data-id="element-2750">
                    <img src={img} alt={`Thumbnail ${idx}`} className="w-full h-full object-cover" data-id="element-2751" />
                  </button>)}
              </div>
            </div>

            {/* Title & Price Mobile (Hidden on Desktop) */}
            <div className="lg:hidden space-y-4" data-id="element-2752">
              <div className="flex justify-between items-start gap-4" data-id="element-2753">
                <h1 className="font-display font-bold text-2xl text-dark-navy" data-id="element-2754">
                  {mockListing.title}
                </h1>
                <div className="flex items-center gap-1.5 bg-gradient-to-r from-primary-indigo/10 to-ai-purple/10 px-3 py-1.5 rounded-full border border-primary-indigo/20" data-id="element-2755">
                  <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-2756" />
                  <span className="text-sm font-bold text-primary-indigo" data-id="element-2757">
                    {mockListing.matchScore}%
                  </span>
                </div>
              </div>
              <div className="font-bold text-3xl text-primary-indigo" data-id="element-2758">
                {mockListing.price}
              </div>
              <div className="flex items-center gap-2 text-slate-500" data-id="element-2759">
                <MapPinIcon className="w-5 h-5" data-id="element-2760" />
                <span data-id="element-2761">{mockListing.location}</span>
              </div>
            </div>

            {/* AI Summary Box */}
            <div className="bg-gradient-to-br from-primary-indigo/5 via-ai-purple/5 to-electric-cyan/5 rounded-2xl p-6 border border-primary-indigo/10 ai-glow" data-id="element-2762">
              <div className="flex items-center gap-3 mb-4" data-id="element-2763">
                <div className="p-2 bg-gradient-to-br from-primary-indigo to-ai-purple rounded-xl" data-id="element-2764">
                  <SparklesIcon className="w-5 h-5 text-white" data-id="element-2765" />
                </div>
                <h3 className="font-display font-bold text-lg text-dark-navy" data-id="element-2766">
                  AI Analysis
                </h3>
              </div>
              <ul className="space-y-3" data-id="element-2767">
                {mockListing.aiSummary.map((item, idx) => <li key={idx} className="flex items-start gap-3 text-slate-700" data-id="element-2768">
                    <CheckCircle2Icon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" data-id="element-2769" />
                    <span data-id="element-2770">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Key Information Grid */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200" data-id="element-2771">
              <h3 className="font-display font-bold text-xl text-dark-navy mb-6" data-id="element-2772">
                Key Information
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-2773">
                {mockListing.specs.map((spec, idx) => <div key={idx} data-id="element-2774">
                    <span className="block text-sm text-slate-500 mb-1" data-id="element-2775">
                      {spec.label}
                    </span>
                    <span className="font-medium text-slate-900" data-id="element-2776">
                      {spec.value}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200" data-id="element-2777">
              <h3 className="font-display font-bold text-xl text-dark-navy mb-4" data-id="element-2778">
                Description
              </h3>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-2779">
                {mockListing.description}
              </p>
            </div>
          </div>

          {/* Right Column: Sticky Sidebar */}
          <div className="space-y-6" data-id="element-2780">
            <div className="sticky top-24 space-y-6" data-id="element-2781">
              {/* Title & Price Desktop (Hidden on Mobile) */}
              <div className="hidden lg:block bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4" data-id="element-2782">
                <div className="flex justify-between items-start gap-4" data-id="element-2783">
                  <h1 className="font-display font-bold text-2xl text-dark-navy" data-id="element-2784">
                    {mockListing.title}
                  </h1>
                  <div className="flex items-center gap-1.5 bg-gradient-to-r from-primary-indigo/10 to-ai-purple/10 px-3 py-1.5 rounded-full border border-primary-indigo/20 flex-shrink-0" data-id="element-2785">
                    <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-2786" />
                    <span className="text-sm font-bold text-primary-indigo" data-id="element-2787">
                      {mockListing.matchScore}%
                    </span>
                  </div>
                </div>
                <div className="font-bold text-3xl text-primary-indigo" data-id="element-2788">
                  {mockListing.price}
                </div>
                <div className="flex items-center gap-2 text-slate-500" data-id="element-2789">
                  <MapPinIcon className="w-5 h-5" data-id="element-2790" />
                  <span data-id="element-2791">{mockListing.location}</span>
                </div>
              </div>

              {/* Seller Card */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200" data-id="element-2792">
                <div className="flex items-center gap-4 mb-6" data-id="element-2793">
                  <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center text-xl font-bold text-slate-400" data-id="element-2794">
                    {mockListing.seller.name.charAt(0)}
                  </div>
                  <div data-id="element-2795">
                    <h3 className="font-bold text-lg text-dark-navy" data-id="element-2796">
                      {mockListing.seller.name}
                    </h3>
                    <div className="flex items-center gap-1.5 text-sm mt-1" data-id="element-2797">
                      <ShieldCheckIcon className="w-4 h-4 text-success" data-id="element-2798" />
                      <span className="font-medium text-success" data-id="element-2799">
                        {mockListing.seller.type}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6 text-sm" data-id="element-2800">
                  <div className="bg-slate-50 p-3 rounded-xl" data-id="element-2801">
                    <span className="block text-slate-500 mb-1" data-id="element-2802">
                      Response Rate
                    </span>
                    <span className="font-medium text-slate-900" data-id="element-2803">
                      {mockListing.seller.responseRate}
                    </span>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-xl" data-id="element-2804">
                    <span className="block text-slate-500 mb-1" data-id="element-2805">Replies</span>
                    <span className="font-medium text-slate-900" data-id="element-2806">
                      {mockListing.seller.responseTime}
                    </span>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-2807">
                  <button className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors shadow-sm" data-id="element-2808">
                    <MessageSquareIcon className="w-5 h-5" data-id="element-2809" />
                    Message Seller
                  </button>
                  <button className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#25D366] text-white rounded-xl font-medium hover:bg-[#20BD5A] transition-colors shadow-sm" data-id="element-2810">
                    <PhoneIcon className="w-5 h-5" data-id="element-2811" />
                    WhatsApp
                  </button>
                </div>
              </div>

              {/* Ask AI Chat Box */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-[400px]" data-id="element-2812">
                <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-primary-indigo/5 to-ai-purple/5 flex items-center gap-2" data-id="element-2813">
                  <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-2814" />
                  <h3 className="font-display font-bold text-dark-navy" data-id="element-2815">
                    Ask AI about this listing
                  </h3>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50" data-id="element-2816">
                  {aiChat.length === 0 ? <div className="space-y-2" data-id="element-2817">
                      <p className="text-sm text-slate-500 mb-3" data-id="element-2818">
                        Suggested questions:
                      </p>
                      {['Is this overpriced?', 'Compare this with my saved car', 'What should I check before buying?'].map((q, i) => <button key={i} onClick={() => setAiQuery(q)} className="block w-full text-left px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors" data-id="element-2819">
                          {q}
                        </button>)}
                    </div> : aiChat.map((msg, idx) => <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-2820">
                        <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${msg.role === 'user' ? 'bg-primary-indigo text-white rounded-tr-none' : 'bg-white border border-slate-200 text-slate-700 rounded-tl-none shadow-sm'}`} data-id="element-2821">
                          {msg.content}
                        </div>
                      </div>)}
                </div>

                <form onSubmit={handleAskAI} className="p-3 border-t border-slate-100 bg-white" data-id="element-2822">
                  <div className="relative" data-id="element-2823">
                    <input type="text" value={aiQuery} onChange={e => setAiQuery(e.target.value)} placeholder="Ask anything..." className="w-full pl-4 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-primary-indigo focus:ring-2 focus:ring-primary-indigo/10 transition-all" data-id="element-2824" />
                    <button type="submit" disabled={!aiQuery.trim()} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 text-primary-indigo hover:bg-primary-indigo/10 rounded-lg transition-colors disabled:opacity-50" data-id="element-2825">
                      <SendIcon className="w-4 h-4" data-id="element-2826" />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}